// Kalender-Abo der eigenen Einsätze (27.10.2026, Betreiber: „Einsatz-Kalender in mein iPhone-Kalender integrieren“).
// Der Server erzeugt einen geheimen Link (backend/einsatz/feed.py); nur die eigenen Einsätze, Kunden nur mit Vornamen.
// Der Link wird nur beim Erzeugen gezeigt — gespeichert ist er nur als Prüfsumme, „Neuer Link“ sperrt den alten.
import { useState } from 'react';
import { CalendarPlus, Copy, Loader2, Smartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { einsatzAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'noch nie');

export default function EinsatzKalenderAbo() {
  const [offen, setOffen] = useState(false);
  const [stand, setStand] = useState(null);
  const [link, setLink] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const oeffnen = async () => {
    setOffen(true); setLink(null);
    try { setStand((await einsatzAPI.aboStand()).data); } catch { toast.error('Stand konnte nicht geladen werden.'); }
  };
  const erzeugen = async () => {
    if (stand?.abo?.erstellt_am && !window.confirm('Neuen Link erzeugen? Der bisherige hört sofort auf zu funktionieren; ein schon eingerichtetes Abo musst du dann neu anlegen.')) return;
    setLaeuft(true);
    try { const r = (await einsatzAPI.aboErzeugen()).data; setLink(r); setStand((s) => ({ ...s, abo: r.abo })); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Link konnte nicht erzeugt werden.'); }
    setLaeuft(false);
  };
  const beenden = async () => {
    if (!window.confirm('Abo beenden? Der Kalender auf dem Handy bekommt danach keine Einsätze mehr.')) return;
    try { await einsatzAPI.aboLoeschen(); setLink(null); setStand((s) => ({ ...s, abo: {} })); toast.success('Abo beendet.'); }
    catch { toast.error('Beenden fehlgeschlagen.'); }
  };
  const kopieren = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Link kopiert.'); } catch { toast.error('Kopieren ging nicht, bitte von Hand markieren.'); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={oeffnen} data-testid="einsatz-abo">
        <CalendarPlus className="w-3.5 h-3.5 mr-1" />Ins Handy
      </Button>
      <Dialog open={offen} onOpenChange={setOffen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Meine Einsätze im iPhone-Kalender</DialogTitle></DialogHeader>
          {!stand ? <Loader2 className="w-5 h-5 animate-spin" /> : !stand.trainer ? (
            <p className="text-sm text-muted-foreground">Zu deinem CRM-Konto gibt es kein Trainer-Konto in der App (gleiche E-Mail, Rolle Trainer oder Leitung). Unter Einsatzplanung › Trainer lässt es sich verknüpfen.</p>
          ) : (
            <div className="min-w-0 space-y-4 text-sm">
              <p className="text-muted-foreground">
                Im Kalender stehen nur deine eigenen Einsätze als <b>{stand.trainer.name}</b>: Kurse (auch Vertretung und Assistenz),
                PT-Termine und übernommene Check-Ups, von einer Woche zurück bis acht Wochen voraus. Kunden nur mit Vornamen.
                Das iPhone holt Änderungen von selbst, meist innerhalb einer Stunde.
              </p>
              {link ? (
                <div className="min-w-0 space-y-3">
                  <div className="mx-auto w-48 rounded-lg bg-white p-2 [&_svg]:h-auto [&_svg]:w-full" dangerouslySetInnerHTML={{ __html: link.qr_svg }} />
                  <p className="text-center text-xs text-muted-foreground">Mit der iPhone-Kamera scannen und „Abonnieren“ tippen.</p>
                  <a href={link.webcal} className="flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-primary-foreground">
                    <Smartphone className="w-4 h-4" />Auf diesem Gerät abonnieren
                  </a>
                  <div className="flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1 text-xs select-all">{link.url}</code>
                    <Button size="sm" variant="outline" onClick={() => kopieren(link.url)}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Von Hand: iPhone › Einstellungen › Kalender › Accounts › Account hinzufügen › Andere › Kalenderabo hinzufügen, Link einfügen.
                    Der Link ist wie ein Schlüssel: nicht weitergeben. Er wird nur jetzt angezeigt.</p>
                </div>
              ) : (
                <Button onClick={erzeugen} disabled={laeuft} className="w-full">
                  {laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-1" />}
                  {stand.abo?.erstellt_am ? 'Neuen Link erzeugen' : 'Link erzeugen'}
                </Button>
              )}
              {stand.abo?.erstellt_am && (
                <div className="flex items-center justify-between gap-2 border-t pt-3 text-xs text-muted-foreground">
                  <span>Abo seit {zeit(stand.abo.erstellt_am)} · zuletzt abgerufen {zeit(stand.abo.letzter_abruf)}</span>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={beenden}><Trash2 className="w-3.5 h-3.5 mr-1" />Beenden</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
